import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Search, Plus, Zap, Loader2, Package, Lock } from "lucide-react";
import { toast } from "sonner";
import useLineDrafts from "./useLineDrafts";
import { LineRow, SupplyCell } from "./LineRow";
import { MobileLineCard } from "./MobileLineCard";
import BulkBar from "./BulkBar";
import TotalsFooter from "./TotalsFooter";
import CurrencyConvertDialog from "./CurrencyConvertDialog";
import SaveSummaryDialog from "./SaveSummaryDialog";
import { OrderLineDialog } from "@/components/OrderLinesEditor";
import { sumByCurrency, supplyPatch } from "@/lib/lineMath";
import { fetchAvailability } from "@/lib/stockAvailability";
import { useAuth } from "@/context/AuthContext";

const SUMMARY_THRESHOLD = 5;
const useIsPhone = () => {
  const q = "(max-width: 767px)";
  const [m, setM] = useState(() => window.matchMedia(q).matches);
  useEffect(() => {
    const mq = window.matchMedia(q);
    const h = () => setM(mq.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);
  return m;
};

const TH = ({ children, right, className = "" }) => (
  <th className={`px-1.5 py-2 text-[10px] uppercase tracking-[0.12em] font-mono font-medium text-slate-500 whitespace-nowrap ${right ? "text-right" : "text-left"} ${className}`}>{children}</th>
);

export default function LinesWorkspace({ order, fx, readOnly, confirmed, changeSummary, onSaved, onDirtyChange, discLimit }) {
  const { can } = useAuth();
  const d = useLineDrafts(order.id);
  const isPhone = useIsPhone();
  const [quick, setQuick] = useState(false);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(() => new Set());
  const [convReq, setConvReq] = useState(null);
  const [sumReq, setSumReq] = useState(null);
  const [detail, setDetail] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [stockMap, setStockMap] = useState({});
  const supplyEditable = ["teklif", "siparis_onay"].includes(order.stage) && !readOnly;
  const pidKey = [...new Set(d.rows.map((r) => r.row.product_id).filter(Boolean))].sort().join(",");
  useEffect(() => {
    if (!pidKey) return;
    fetchAvailability({ productIds: pidKey.split(",") }).then((rows) => {
      const m = {};
      for (const a of rows) m[`${a.product_id}|${a.variant_id || ""}|${a.warehouse_id}`] = a;
      setStockMap(m);
    }).catch(() => {});
  }, [pidKey]);
  const stockOf = (r) => stockMap[`${r.product_id}|${r.variant_id || ""}|${r.warehouse_id || ""}`];
  const stockRef = useRef(stockOf);
  stockRef.current = stockOf;

  useEffect(() => { d.load(); api.get("/warehouses").then((r) => setWarehouses(r.data || [])).catch(() => {}); /* eslint-disable-next-line */ }, [order.id]);
  useEffect(() => { onDirtyChange?.(d.dirtyRows.length); }, [d.dirtyRows.length, onDirtyChange]);

  const rowById = useMemo(() => Object.fromEntries(d.rows.map((r) => [r.row.id, r.row])), [d.rows]);
  const rowByIdRef = useRef(rowById);
  rowByIdRef.current = rowById;
  const visible = useMemo(() => {
    const t = q.trim().toLocaleLowerCase("tr");
    if (!t) return d.rows;
    return d.rows.filter(({ row }) => `${row.product_name} ${row.variant_sku || ""} ${Object.values(row.variant_attributes || {}).join(" ")}`
      .toLocaleLowerCase("tr").includes(t));
  }, [d.rows, q]);
  const totals = useMemo(() => sumByCurrency(d.rows.filter((r) => !r.row._deleted).map((r) => r.row)), [d.rows]);
  const before = useMemo(() => sumByCurrency(Object.values(d.origMap)), [d.origMap]);

  const toggle = useCallback((id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; }), []);
  const allVisibleSelected = visible.length > 0 && visible.every(({ row }) => sel.has(row.id));
  const toggleAll = () => setSel(allVisibleSelected ? new Set() : new Set(visible.map(({ row }) => row.id)));

  const { patch, bulkPatch } = d;
  const onCurrency = useCallback((ids, to) => {
    const rows = ids.map((id) => rowByIdRef.current[id]).filter((r) => r && (r.currency || "TRY") !== to);
    if (!rows.length) return;
    const priced = rows.filter((r) => Number(r.unit_price) > 0 || Number(r.discount_amount) > 0 || (r.applications || []).length);
    if (!priced.length) { bulkPatch(rows.map((r) => r.id), { currency: to }); return; }
    setConvReq({ rows, to });
  }, [bulkPatch]);
  const onDelete = useCallback((id) => patch(id, { _deleted: true }), [patch]);
  const onSupply = useCallback((id, m) => {
    const r = rowByIdRef.current[id];
    if (!r) return;
    if (!m) { patch(id, { stock_qty: null, remaining_supply_method: "" }); return; }
    if ((m === "stock" || m === "stock_mix") && !r.warehouse_id) { toast.error(`${r.product_name}: Stoktan Satış için önce Depo sütunundan depo seçin`); return; }
    patch(id, supplyPatch(r, m, stockRef.current(r)?.available_physical));
  }, [patch]);
  const bulkSupply = (m) => { for (const id of selIds) onSupply(id, m); };
  const whEditable = !readOnly && can("warehouse", "view");
  const onWarehouse = useCallback((id, w) => {
    patch(id, { warehouse_id: w?.id || "", warehouse_name: w?.name || "" });
  }, [patch]);
  const bulkWarehouse = (wid) => {
    const w = warehouses.find((x) => x.id === wid);
    if (!w) return;
    const locked = selIds.filter((id) => Number(rowById[id].reserved_qty || 0) > 0 || Number(rowById[id].reserved_incoming_qty || 0) > 0);
    const ok = selIds.filter((id) => !locked.includes(id));
    if (ok.length) bulkPatch(ok, { warehouse_id: w.id, warehouse_name: w.name });
    if (locked.length) toast.warning(`${locked.length} rezerveli satırın deposu değiştirilmedi`);
  };
  const onUndoDelete = useCallback((id) => patch(id, { _deleted: false }), [patch]);
  const onDetail = useCallback((id) => setDetail({ mode: "edit", id }), []);

  const doSave = async () => {
    if (confirmed && !changeSummary.trim()) { toast.error("Üretimdeki siparişte Revizyon Sebebi zorunludur (Genel Bilgiler)"); return; }
    if (await d.save(changeSummary)) { setSumReq(null); setSel(new Set()); onSaved?.(); }
  };
  const requestSave = () => {
    if (d.dirtyRows.length >= SUMMARY_THRESHOLD) setSumReq(d.summary());
    else doSave();
  };

  const applyDetail = (form) => {
    const { id, ...rest } = form;
    if (detail.mode === "new") d.addRow(rest);
    else d.patch(detail.id, rest);
    setDetail(null);
  };
  const detailLine = detail?.mode === "edit" ? rowById[detail.id] : detail?.mode === "new" ? { quantity: 1, unit_price: 0, currency: "TRY", tax_rate: 20, discount_percent: 0, discount_amount: 0, applications: [], notes: "" } : null;

  const selIds = [...sel].filter((id) => rowById[id]);
  return (
    <div className="flex flex-col h-full min-h-0" data-testid="order-lines-workspace">
      <div className="shrink-0 flex flex-wrap items-center gap-2 px-4 sm:px-6 py-2.5 border-b border-slate-800">
        <div className="relative w-full sm:w-72">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Satırlarda ürün / beden ara" data-testid="ws-search"
            className="w-full h-8 pl-8 pr-2 bg-slate-900 border border-slate-800 rounded-sm text-xs outline-none focus:border-blue-500" />
        </div>
        {!isPhone && (
          <button type="button" onClick={() => setQuick((v) => !v)} data-testid="ws-quick-toggle" aria-pressed={quick}
            className={`h-8 px-3 rounded-sm text-[11px] uppercase tracking-wider font-semibold inline-flex items-center gap-1.5 border transition-colors
              ${quick ? "bg-amber-500 text-slate-950 border-amber-400" : "border-slate-700 text-slate-300 hover:bg-slate-800"}`}>
            <Zap className="w-3.5 h-3.5" /> Hızlı Fiyat Düzenle
          </button>
        )}
        {!readOnly && (
          <button type="button" onClick={() => setDetail({ mode: "new" })} data-testid="add-order-line-btn"
            className="h-8 px-3 rounded-sm text-[11px] uppercase tracking-wider font-semibold inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 text-white">
            <Plus className="w-3.5 h-3.5" /> Satır Ekle
          </button>
        )}
        <div className="ml-auto text-[11px] font-mono text-slate-500 hidden md:block" data-testid="ws-row-count">
          {d.rows.length} satır{quick && " · Enter: alt satır · Tab: sonraki alan · Esc: iptal"}
        </div>
      </div>
      {readOnly && (
        <div className="shrink-0 px-6 py-1.5 text-[11px] text-slate-400 bg-slate-900/60 border-b border-slate-800 flex items-center gap-1.5" data-testid="ws-readonly">
          <Lock className="w-3 h-3" /> Kesilmiş fatura belgesinin satırları bu ekrandan değiştirilemez (yalnız görüntüleme).
        </div>
      )}
      {!isPhone && !readOnly && (
        <BulkBar count={selIds.length} onClear={() => setSel(new Set())}
          onTax={(v) => d.bulkPatch(selIds, { tax_rate: v })}
          onDisc={(v) => d.bulkPatch(selIds, { discount_percent: v })}
          onCurrency={(c) => onCurrency(selIds, c)} onSupply={supplyEditable ? bulkSupply : null}
          warehouses={whEditable ? warehouses : null} onWarehouse={bulkWarehouse} />
      )}

      <div className="flex-1 min-h-0 overflow-auto overscroll-contain" data-testid="ws-scroll">
        {d.loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-blue-500 animate-spin" /></div>
        ) : d.rows.length === 0 ? (
          <div className="py-16 text-center text-sm text-slate-500" data-testid="lines-empty">
            <Package className="w-9 h-9 mx-auto mb-2 text-slate-700" /> Henüz satır yok. "Satır Ekle" ile başlayın.
          </div>
        ) : isPhone ? (
          <div data-testid="ws-cards">
            {visible.map(({ row, sig }) => (
              <div key={row.id}>
                <MobileLineCard row={row} changedSig={sig} onOpen={onDetail} />
                {(supplyEditable || Number(row.stock_qty || 0) > 0) && (
                  <div className="px-3.5 pb-2 -mt-1 border-b border-slate-800">
                    <SupplyCell row={row} stock={stockMap[`${row.product_id}|${row.variant_id || ""}|${row.warehouse_id || ""}`]}
                      editable={supplyEditable && !row._deleted} changed={sig.includes("stock_qty")} onSupply={onSupply} />
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <table className="w-full border-collapse min-w-[1080px]" data-testid="ws-table">
            <thead className="sticky top-0 z-10 bg-slate-950 shadow-[0_1px_0_0_rgb(30,41,59)]">
              <tr>
                <TH><input type="checkbox" checked={allVisibleSelected} onChange={toggleAll} disabled={readOnly} data-testid="ws-select-all" className="accent-blue-500 w-3.5 h-3.5" /></TH>
                <TH>Ürün</TH><TH>Beden</TH>{!quick && <TH>Depo</TH>}{!quick && <TH>Tedarik</TH>}
                <TH right>Adet</TH><TH>PB</TH>
                {quick && <TH right>Eski Fiyat</TH>}
                <TH right className={quick ? "text-amber-300" : ""}>{quick ? "Yeni Fiyat" : "Birim Fiyat"}</TH>
                <TH right>İsk. %</TH><TH right>KDV %</TH>
                {!quick && <TH right>Ara Toplam</TH>}
                <TH right>{quick ? "Yeni Toplam" : "Toplam"}</TH>
                {!quick && <TH right>İşlem</TH>}
              </tr>
            </thead>
            <tbody>
              {visible.map(({ row, sig }, i) => (
                <LineRow key={row.id} row={row} orig={d.origMap[row.id]} idx={i} quick={quick} selected={sel.has(row.id)}
                  changedSig={sig} readOnly={readOnly} discLimit={discLimit}
                  stock={stockMap[`${row.product_id}|${row.variant_id || ""}|${row.warehouse_id || ""}`]}
                  supplyEditable={supplyEditable} onSupply={onSupply}
                  warehouses={warehouses} stockMap={stockMap} whEditable={whEditable} onWarehouse={onWarehouse}
                  onPatch={d.patch} onCurrency={onCurrency} onToggle={toggle} onDetail={onDetail}
                  onDelete={onDelete} onUndoDelete={onUndoDelete} />
              ))}
            </tbody>
          </table>
        )}
      </div>

      <TotalsFooter totals={totals} before={before} dirtyCount={d.dirtyRows.length} saving={d.saving} readOnly={readOnly}
        onDiscard={() => { if (window.confirm("Kaydedilmemiş tüm değişiklikler geri alınsın mı?")) d.discard(); }} onSave={requestSave} />

      <CurrencyConvertDialog req={convReq} fx={fx} onCancel={() => setConvReq(null)}
        onConfirm={(ids, to) => { d.convert(ids, to, fx, rowById); setConvReq(null); }} />
      <SaveSummaryDialog summary={sumReq} saving={d.saving} onCancel={() => setSumReq(null)} onConfirm={doSave} />
      <OrderLineDialog open={!!detail} mode={detail?.mode === "new" ? "new" : "edit"} line={detailLine} draftKey={detail ? `${detail.mode}-${detail.id || ""}` : ""}
        warehouses={warehouses} orderId={order.id} customerId={order.customer_id} fxRates={fx} readOnly={readOnly}
        supplyEditable={supplyEditable} supplyIsOrder={order.stage !== "teklif"}
        persisted={detail?.mode === "edit" && !String(detail.id).startsWith("new-")}
        onOpenChange={(v) => !v && setDetail(null)} onApply={applyDetail}
        onDelete={(id) => { onDelete(id); setDetail(null); }} onAttachmentsChanged={d.refresh} />
    </div>
  );
}
